import type { Volume } from '../types/content';

export const volumes: Volume[] = [
  {
    numeral: 'I',
    title: 'The Hair Transplant Patient Assessment Blueprint',
    tagline: 'Organize the Information Relevant to Hair Restoration Assessment.',
    description: [
      'A structured educational approach to patient assessment begins with understanding the categories of information that may be relevant to evaluation and professional documentation.',
      'This volume introduces frameworks for organizing patient history, hair loss characteristics, donor area observations, expectations, and factors that may require additional assessment.',
    ],
    explore: [
      'Patient history review frameworks.',
      'Educational hair loss assessment worksheets.',
      'Hair loss pattern documentation.',
      'Donor area assessment considerations.',
      'Patient expectations and consultation notes.',
      'Factors that may warrant further professional evaluation.',
      'Structured patient information review templates.',
    ],
    note: 'The goal is to help you organize relevant information for professional assessment within your scope of practice.',
    accent: '#2f86ec',
  },
  {
    numeral: 'II',
    title: 'The Follicular Unit Anatomy & Hair Restoration Atlas',
    tagline: 'Build a More Organized Foundation for Studying Hair and Scalp Anatomy.',
    description: [
      'Understanding hair and scalp anatomy provides essential context for studying hair restoration.',
      'This volume organizes anatomical concepts and educational references into a visual study resource for qualified professionals.',
    ],
    explore: [
      'Hair follicle anatomy.',
      'Follicular unit structure.',
      'Hair growth cycles and related concepts.',
      'Scalp anatomy references.',
      'Hair shaft characteristics.',
      'Differences in hair characteristics and density.',
      'Anatomical variation and individual differences.',
      'Visual learning references for continuing education.',
    ],
    note: 'This volume supports anatomical learning. It does not replace supervised procedural training or establish universally appropriate surgical techniques.',
    accent: '#1fa772',
  },
  {
    numeral: 'III',
    title: 'The Hairline Design & Aesthetic Planning Blueprint',
    tagline: 'Study the Educational Principles Behind Hairline Design.',
    description: [
      'Hairline design involves more than choosing a line on the scalp.',
      'It requires consideration of facial proportions, individual characteristics, existing hair distribution, aesthetic expectations, and the broader context of hair restoration planning.',
      'This volume organizes these concepts into an educational reference for professional study.',
    ],
    explore: [
      'Fundamental hairline design concepts.',
      'Facial proportions and aesthetic considerations.',
      'Natural hairline variation.',
      'Age-related considerations in hair restoration planning.',
      'Hair direction and growth pattern concepts.',
      'Educational examples of different hairline configurations.',
      'Patient expectation documentation.',
      'Case-based hairline design discussions.',
    ],
    note: 'The materials are intended to support education and professional discussion, not to prescribe a specific hairline or guarantee an aesthetic outcome.',
    accent: '#d39a2c',
  },
  {
    numeral: 'IV',
    title: 'The Donor Area Assessment & Follicular Resource Atlas',
    tagline: 'Understand the Educational Principles Behind Donor Area Assessment.',
    description: [
      'Donor characteristics are an important consideration in hair restoration planning.',
      'This volume brings together educational references on scalp characteristics, follicular distribution, donor area evaluation, and the factors professionals study when considering the available donor resource.',
    ],
    explore: [
      'Donor area anatomy references.',
      'Follicular unit distribution concepts.',
      'Hair density and caliber considerations.',
      'Donor characteristics and individual variation.',
      'Scalp assessment documentation.',
      'Educational comparisons of donor area characteristics.',
      'Factors that may warrant further evaluation.',
      'Donor area photographic documentation frameworks.',
    ],
    note: 'These resources support educational review and do not replace individualized medical assessment or qualified surgical judgment.',
    accent: '#8b5cf6',
  },
  {
    numeral: 'V',
    title: 'The Hair Transplant Complications & Safety Reference',
    tagline: 'Organize Your Study of Potential Complications and Safety Considerations.',
    description: [
      'Hair restoration procedures require appropriate professional preparation, awareness of potential complications, and an understanding of when clinical findings require further evaluation.',
      'This volume organizes educational references covering selected complications, concerning presentations, documentation, and professional escalation considerations.',
    ],
    explore: [
      'General categories of potential complications.',
      'Common postoperative symptoms and observations.',
      'Educational references on infection-related concerns.',
      'Wound healing and recovery considerations.',
      'Symptoms that may require prompt medical evaluation.',
      'Documentation of symptom onset and progression.',
      'Educational case scenarios.',
      'Referral and escalation considerations.',
    ],
    note: 'This volume is an educational resource. It does not replace current clinical guidelines, emergency protocols, professional training, or individualized clinical management.',
    accent: '#e5534b',
  },
  {
    numeral: 'VI',
    title: 'The Hair Transplant Documentation & Follow-Up Blueprint',
    tagline: 'Bring Greater Structure to Your Professional Documentation.',
    description: [
      'Patient information, photographic records, procedure documentation, and follow-up notes can involve multiple documents and information sources.',
      'This volume focuses on organizing educational documentation resources that can be adapted to an appropriate professional setting.',
    ],
    explore: [
      'Patient history and assessment templates.',
      'Consultation documentation worksheets.',
      'Hair and scalp assessment records.',
      'Photographic documentation checklists.',
      'Procedure record organization.',
      'Follow-up documentation templates.',
      'Patient communication records.',
      'Adverse-event documentation forms.',
      'Structured clinical review worksheets.',
    ],
    note: 'All templates should be adapted to your professional scope, applicable privacy requirements, and local regulations.',
    accent: '#2cc0ff',
  },
];
